// screen: Registered viewers — settings (change viewer-crm)
// Registered viewers — settings (change viewer-crm, design §4.6): a link to viewer-accounts' retention setting,
// privacy-request deadline (30 days by default — to be confirmed by a legal adviser), who may export
// (Admins and Owners, or Owners only). Read with viewers:read, saved with viewers:manage.
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import type { ViewerSettings } from '@viewstream/api-client';
import { Button, Field, Input, Select, Skeleton, useToast } from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { can } from '~/app/permissions';
import { problemText } from '~/app/ingest';
import { useSaveViewerSettings, useViewerSettings } from '~/app/viewers';
import { ViewersHeader, VIEWERS_MANAGE } from '~/app/ViewersNav';

export const Route = createFileRoute('/_layout/t/$slug/viewers/settings')({ component: Screen });

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  React.useEffect(() => { document.title = appTitle(t`Registered viewers: settings`); }, [t]);
  const cur = useViewerSettings(tenant.slug);
  const save = useSaveViewerSettings(tenant.slug);
  const [s, setS] = React.useState<ViewerSettings | null>(null);
  React.useEffect(() => { if (cur.data && !s) setS(cur.data.settings); }, [cur.data, s]);
  const canEdit = can(scopes, VIEWERS_MANAGE);
  if (cur.isPending || !s) return <div><ViewersHeader tab="settings" /><Skeleton className="h-40 w-full" /></div>;
  return (
    <div>
      <ViewersHeader tab="settings" />
      <div className="max-w-xl space-y-4">
        <p className="text-sm" data-testid="viewer-retention-link">
          {t`Inactive accounts are deleted after the period set in Settings → General → Viewer accounts (24 months by default; a warning e-mail goes out 30 days before).`}{' '}
          <Link to={'/t/$slug/settings' as never} params={{ slug: tenant.slug } as never} className="text-primary hover:underline">{t`Open the settings`}</Link>
        </p>
        <Field id="vs-sla" label={t`Answer privacy requests within (days)`} hint={t`30 days by default. Confirm the deadline with your legal adviser.`}>
          <Input id="vs-sla" type="number" min={1} max={90} value={s.request_sla_days} disabled={!canEdit} onChange={(e) => setS({ ...s, request_sla_days: Number(e.target.value) })} />
        </Field>
        <Field id="vs-export" label={t`Who may export viewers`} hint={t`Exports contain only viewers who agreed to marketing e-mails.`}>
          <Select ariaLabel={t`Who may export viewers`} value={s.export_role} onValueChange={(v) => setS({ ...s, export_role: v as ViewerSettings['export_role'] })}
            options={[{ value: 'admin', label: t`Admins and Owners` }, { value: 'owner', label: t`Owners only` }]} />
        </Field>
        <p className="text-sm text-fg-muted">{t`Before real viewers register, your organisation and Interhost sign a data-processing agreement: you decide about your viewers' data, Interhost processes it for you.`}</p>
        {canEdit ? (
          <Button disabled={save.isPending} onClick={() => save.mutate(s, {
            onSuccess: () => toast.push({ title: t`Saved` }),
            onError: (e) => toast.push({ title: t`Something went wrong`, description: problemText(e), tone: 'danger' }),
          })} data-testid="viewer-settings-save">{t`Save`}</Button>
        ) : null}
      </div>
    </div>
  );
}
